import { lazy, Suspense, useEffect } from 'react';
import { isLegacyDesign } from 'newLook/featureFlag';
import NewLookApp from 'newLook/NewLookApp';

// Loaded on demand so new-look visitors don't download the legacy stack.
const LegacyApp = lazy(() => import('./LegacyApp'));
const LmsProductScreen = lazy(() => import('components/LmsProductScreen'));

// Compasso now lives on the main site at /compasso. The old subdomain
// (compasso.femtowareinfotech.com) forwards there so existing links keep working.
// Locally, use compasso.localhost:3000 or append ?compasso to any URL in dev.
const isCompassoSubdomain = () => {
	const { hostname, search } = window.location;
	if (hostname.startsWith('compasso.')) return true;
	return import.meta.env.DEV && new URLSearchParams(search).has('compasso');
};

// Lumen (the LMS product) renders on its own subdomain (lms.femtowareinfotech.com).
// Locally, use lms.localhost:3000 or append ?lms to any URL in dev.
const isLmsSubdomain = () => {
	const { hostname, search } = window.location;
	if (hostname.startsWith('lms.')) return true;
	return import.meta.env.DEV && new URLSearchParams(search).has('lms');
};

const COMPASSO_URL = import.meta.env.DEV ? '/compasso' : 'https://femtowareinfotech.com/compasso';

const CompassoRedirect = () => {
	useEffect(() => {
		window.location.replace(COMPASSO_URL);
	}, []);
	return null;
};

/**
 * Renders the new-look design by default. The legacy design is preserved and
 * shown when the local feature flag is enabled — see `newLook/featureFlag`.
 * The Lumen (LMS) subdomain always renders its dedicated screen; the old
 * Compasso subdomain forwards to /compasso on the main site.
 */
const App = () => {
	if (isCompassoSubdomain()) {
		return <CompassoRedirect />;
	}

	if (isLmsSubdomain()) {
		return (
			<Suspense fallback={null}>
				<LmsProductScreen />
			</Suspense>
		);
	}

	if (isLegacyDesign()) {
		return (
			<Suspense fallback={null}>
				<LegacyApp />
			</Suspense>
		);
	}

	return <NewLookApp />;
};

export default App;
